import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  normalizeStaffFromFirestore,
  pickPostLoginPath
} from '../config/staffFeatures';
import {
  persistAllowedFeatures,
  filterValidStaffFeatureIds
} from '../utils/staffAccess';
import { clearStaffSessionStorage } from '../utils/staffSessionGuard';
import './Login.css';

const ALLOWED_ROLES = new Set(['admin', 'staff', 'viewer', 'cashier', 'qrscanner']);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        clearStaffSessionStorage();
        setError('No se encontró el rol del usuario.');
        return;
      }

      const data = userDoc.data() || {};

      // Cortocircuito de seguridad: si el admin lo deshabilitó, no dejar entrar.
      if (data.authDisabled === true) {
        clearStaffSessionStorage();
        try { await auth.signOut(); } catch (_) { /* noop */ }
        setError('Esta cuenta fue deshabilitada. Contactá al administrador.');
        return;
      }

      const { role, features } = normalizeStaffFromFirestore(data);
      if (!ALLOWED_ROLES.has(role)) {
        clearStaffSessionStorage();
        try { await auth.signOut(); } catch (_) { /* noop */ }
        setError('No tenés permiso para acceder a esta aplicación.');
        return;
      }

      const cleanFeatures = filterValidStaffFeatureIds(features);
      const isAdmin = role === 'admin';

      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userRole', role);
      localStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');
      persistAllowedFeatures(isAdmin ? [] : cleanFeatures);

      // Si es staff/viewer sin features, no tiene a dónde ir → rechazar.
      if (!isAdmin && role !== 'cashier' && role !== 'qrscanner' && cleanFeatures.length === 0) {
        clearStaffSessionStorage();
        try { await auth.signOut(); } catch (_) { /* noop */ }
        setError('Tu usuario no tiene accesos habilitados. Pedile al administrador que te asigne permisos.');
        return;
      }

      const target = pickPostLoginPath(role, cleanFeatures);
      navigate(target);
      window.location.reload();
    } catch (err) {
      setError('Email o contraseña incorrectos.');
    }
  };

  return (
    <main className="login-page">
      <section className="login-container" aria-labelledby="login-title">
        <div className="login-header">
          <span className="login-eyebrow">Allegra Club</span>
          <h2 id="login-title" className="login-title">Acceso privado</h2>
          <p className="login-subtitle">Acceso exclusivo para Staff y Admins.</p>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          <label className="login-field">
            <span>Email</span>
            <input
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="login-input"
              autoComplete="email"
            />
          </label>

          <label className="login-field">
            <span>Contraseña</span>
            <input
              type="password"
              placeholder="Ingresá tu contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="login-input"
              autoComplete="current-password"
            />
          </label>

          <button type="submit" className="login-button">Entrar</button>
          {error && <p className="login-error">{error}</p>}
        </form>

        <p className="login-footnote">
          Solo para usuarios autorizados. Si querés tu propia app, comunicate con el desarrollador a{' '}
          <a href="mailto:mariano@priball.com">mariano@priball.com</a>.
        </p>
      </section>
    </main>
  );
};

export default Login;
